/**
 * rentalHold — ค-1: ของที่ "แสดงว่าพร้อมใช้งาน" แต่ยังผูกกับสัญญาอื่นที่ยังไม่จบ
 * (DRAFT/ACTIVE/PENDING_CANCELLATION) อยู่ · backend คำนวณ + ส่งข้อความไทย (rental_hold.message)
 * มาให้แล้ว — ที่นี่แค่แสดงผล **ห้าม derive เงื่อนไขเอง** (ดู types/rentalHold.ts)
 *
 * ต่างจาก `devicePickerFilter`/`simAvailability` (ที่ "ซ่อน" ตัวเลือกที่มีใบตามเก็บค้าง — ด่าน L2):
 * ของที่มี rental_hold ต้อง **ยังเลือกได้อยู่เสมอ** เพียงแต่ต้องติดป้ายเตือนก่อนเลือก เพราะ
 * (1) เดิมของกลุ่มนี้เลือกได้แล้วกดบันทึกไปโดน 400 อย่างไม่มีคำอธิบาย (เคสจริง: ซิม 0834965065)
 * (2) บางกรณี rental_hold ชี้กลับมาที่ "สัญญาที่กำลังแก้ไขอยู่เอง" (เช่นสัญญา DRAFT ที่ยังไม่อนุมัติ
 *     — สถานะเครื่อง/ซิมจึงยัง AVAILABLE) ซึ่งไม่ใช่ปัญหาจริง ไม่ควรเตือน
 */
import type { PickerBlockedItem, RentalHoldInfo } from '../types/rentalHold';

export interface RentalHoldLike {
  rental_hold?: RentalHoldInfo | null;
}

/**
 * คืน `rental_hold` ที่ "ต้องเตือนผู้ใช้จริง ๆ" — null เมื่อไม่มี hold เลย หรือ hold นั้นเป็นของ
 * สัญญาที่กำลังแก้ไขอยู่เอง (`currentRentalId`)
 *
 * ฟอร์มสร้างใหม่ไม่มีสัญญาของตัวเอง (ไม่ส่ง currentRentalId เลย) → hold ที่เจอต้องเป็นของสัญญาอื่น
 * เสมอ ส่วนฟอร์มแก้ไขส่ง id ของสัญญาที่กำลังแก้ (route param เป็น string ก็ได้ — เทียบด้วย String()
 * กันปัญหาชนิดตัวเลข/สตริงไม่ตรงกัน)
 */
export function rentalHoldFor(
  item: RentalHoldLike | null | undefined,
  currentRentalId?: number | string | null,
): RentalHoldInfo | null {
  const hold = item?.rental_hold;
  if (!hold) return null;
  if (
    currentRentalId !== undefined &&
    currentRentalId !== null &&
    currentRentalId !== '' &&
    String(hold.rental_id) === String(currentRentalId)
  ) {
    return null;
  }
  return hold;
}

/**
 * ต่อป้าย "🔒 ผูกกับสัญญา …" เข้าท้ายป้ายเดิมของตัวเลือกใน dropdown — ใช้แทนการซ่อนตัวเลือกทิ้ง
 * (การซ่อนคือบั๊กคลาสที่ fix ค กำลังปิดอยู่พอดี)
 */
export function withRentalHoldLabel(
  label: string,
  item: RentalHoldLike | null | undefined,
  currentRentalId?: number | string | null,
): string {
  const hold = rentalHoldFor(item, currentRentalId);
  if (!hold) return label;
  const rentalRef = hold.rental_number || `#${hold.rental_id}`;
  return `${label} — 🔒 ผูกกับสัญญา ${rentalRef} (${hold.rental_status_label})`;
}

/**
 * ค-2 — `emptyMessage` ของ `SearchableSelect` เมื่อ "ไม่มีตัวเลือกที่เลือกได้ตรงกับคำค้น" แต่ backend
 * เจอของที่มีอยู่จริงแล้วบอกเหตุผลว่าทำไมเลือกไม่ได้ (`blocked[]`) — แทนที่จะให้ผู้ใช้เห็นแค่
 * "ไม่พบ…" ลอย ๆ (`emptyMessage` รองรับหลายบรรทัดผ่าน `whitespace-pre-line` อยู่แล้ว)
 *
 * ใช้เฉพาะตอน "ไม่มีตัวเลือกเหลือให้เลือกแล้ว" — ถ้ายังมีตัวเลือกอื่นให้เลือกอยู่ ผู้เรียกไม่ควรส่ง
 * `blocked` เข้ามา (ยังมีของให้เลือก ไม่ต้องแทนที่ emptyMessage เดิม)
 */
export function blockedEmptyMessage(
  blocked: PickerBlockedItem[] | null | undefined,
  fallback: string,
): string {
  const rows = (blocked || []).slice(0, 5);
  if (rows.length === 0) return fallback;
  return rows.map((b) => `พบ ${b.label} แต่ใช้ไม่ได้:\n${b.reason}`).join('\n\n');
}
